import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Activity, Loader2 } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import * as api from "@/lib/api";
import { ApiError } from "@/lib/api";
import { queryKeys, useAuthStatus } from "@/lib/queries";

function LoginShell({ children }: { children: ReactNode }) {
  return (
    <main className="relative grid min-h-dvh place-items-center overflow-hidden bg-background px-4 py-10 text-foreground">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-48 -top-48 size-96 rounded-full bg-status-up/10 blur-3xl" />
        <div className="absolute -bottom-48 -right-48 size-96 rounded-full bg-status-up/10 blur-3xl" />
      </div>
      {children}
    </main>
  );
}

function Brand() {
  return (
    <div className="mb-2 flex items-center gap-2.5">
      <div className="grid size-9 place-items-center rounded-lg bg-status-up/15">
        <Activity className="size-5 text-status-up" />
      </div>
      <span className="text-lg font-semibold tracking-tight">Vigil</span>
    </div>
  );
}

function LoginLoading() {
  return (
    <LoginShell>
      <Card className="relative w-full max-w-sm shadow-xl shadow-black/5">
        <CardHeader className="space-y-3">
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-4 w-56" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    </LoginShell>
  );
}

export default function LoginPage() {
  const auth = useAuthStatus();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // React Router owns this navigation state; this names the only shape the login flow consumes.
  const state = location.state as { from?: string } | null;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.login(username, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth });
      navigate(state?.from ?? "/dashboard", { replace: true });
    },
  });

  if (auth.isLoading) return <LoginLoading />;
  if (auth.data?.needsSetup) return <Navigate to="/setup" replace />;
  if (auth.data?.authenticated) return <Navigate to="/dashboard" replace />;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!username || !password || mutation.isPending) return;
    mutation.mutate();
  }

  const error =
    mutation.error instanceof ApiError && mutation.error.status === 401
      ? "Invalid username or password"
      : mutation.error instanceof ApiError
        ? mutation.error.message
        : mutation.error?.message;

  return (
    <LoginShell>
      <Card className="relative w-full max-w-sm shadow-xl shadow-black/5">
        <CardHeader>
          <Brand />
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Sign in to manage your monitors.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  if (mutation.error) mutation.reset();
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (mutation.error) mutation.reset();
                }}
                aria-invalid={Boolean(error)}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button className="w-full" type="submit" disabled={!username || !password || mutation.isPending}>
              {mutation.isPending && <Loader2 className="animate-spin" />}
              {mutation.isPending ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="justify-center border-t">
          <p className="text-xs text-muted-foreground">Vigil · self-hosted uptime monitoring</p>
        </CardFooter>
      </Card>
    </LoginShell>
  );
}
